import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantSwitcherApi } from '../api/tenantSwitcherApi';
import type {
  TenantSwitchRequest,
  TenantSampleCreateRequest,
  SampleSearchCondition,
} from '../types/tenantSwitcher';

// ──────────────────────────────────────────────
// 쿼리 키 패턴: ['tenant-switcher', '...', filters]
// ──────────────────────────────────────────────

/** GET /api/eraf/tenant-switcher/context — 현재 테넌트 컨텍스트 */
export function useTenantContext() {
  return useQuery({
    queryKey: ['tenant-switcher', 'context'],
    queryFn: () => tenantSwitcherApi.getContext(),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      // 403/401 은 재시도 없음
      const code = (error as { code?: string })?.code ?? '';
      if (code.startsWith('AUTH-403') || code.startsWith('AUTH-401')) return false;
      return failureCount < 2;
    },
  });
}

/** GET /api/eraf/tenant-switcher/tenants — 전환 가능 테넌트 목록 */
export function useTenants() {
  return useQuery({
    queryKey: ['tenant-switcher', 'tenants'],
    queryFn: () => tenantSwitcherApi.getTenants(),
    staleTime: 60_000,
  });
}

/** GET /api/eraf/tenant-switcher/routing — 읽기/쓰기 라우팅 상태 */
export function useRoutingStatus() {
  return useQuery({
    queryKey: ['tenant-switcher', 'routing'],
    queryFn: () => tenantSwitcherApi.getRoutingStatus(),
    staleTime: 60_000,
  });
}

/** GET /api/eraf/tenant-switcher/samples — 테넌트 격리 표본 목록 */
export function useTenantSamples(filters: SampleSearchCondition) {
  return useQuery({
    queryKey: ['tenant-switcher', 'samples', 'list', filters],
    queryFn: () => tenantSwitcherApi.listSamples(filters),
    staleTime: 30_000,
  });
}

/** POST /api/eraf/tenant-switcher/context — 테넌트 전환 */
export function useSwitchTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: TenantSwitchRequest) => tenantSwitcherApi.switchTenant(req),
    onSuccess: () => {
      // 테넌트 전환 후 컨텍스트 + 테넌트 목록 + 표본 목록 모두 무효화
      void qc.invalidateQueries({ queryKey: ['tenant-switcher', 'context'] });
      void qc.invalidateQueries({ queryKey: ['tenant-switcher', 'tenants'] });
      void qc.invalidateQueries({ queryKey: ['tenant-switcher', 'samples'] });
    },
  });
}

/** POST /api/eraf/tenant-switcher/samples — 표본 생성 (WRITE 라우팅) */
export function useCreateSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: TenantSampleCreateRequest) => tenantSwitcherApi.createSample(req),
    onSuccess: () => {
      // 생성 후 표본 목록 + 테넌트 목록 (sampleCount 갱신) 무효화
      void qc.invalidateQueries({ queryKey: ['tenant-switcher', 'samples'] });
      void qc.invalidateQueries({ queryKey: ['tenant-switcher', 'tenants'] });
    },
  });
}
